<!-- ========================================
     FILE PREVIEW PANEL COMPONENT
     ======================================== -->
<div class="file-preview__container" role="complementary" aria-label="File Preview Panel">

  <!-- ========================================
       PANEL HEADER
       ======================================== -->
  <header class="file-preview__header">
    <div class="file-preview__title">
      <img [src]="getFileIcon()" alt="File Icon" class="file-preview__icon" role="img" aria-hidden="true" *ngIf="previewData">
      <span class="file-preview__title-text">{{ previewData?.fileName || 'File Preview' }}</span>
      <span class="file-preview__size" aria-label="File size" *ngIf="previewData">
        {{ formatFileSize(previewData.fileSize) }}
      </span>
    </div>

    <button class="file-preview__close" (click)="closePanel()" title="Close preview panel"
      aria-label="Close file preview panel" type="button">
      ×
    </button>
  </header>

  <!-- ========================================
       TOOLBAR
       ======================================== -->
  <section class="file-preview__toolbar" aria-label="Preview controls" *ngIf="!isLoading && !errorMessage && previewData">
    <div class="file-preview__toolbar-group">
      <!-- Zoom Controls -->
      <div class="file-preview__zoom-controls">
        <button class="file-preview__zoom-btn" (click)="zoomOut()" 
                [disabled]="zoomLevel <= 50"
                title="Zoom out (Ctrl + -)" 
                aria-label="Zoom out" 
                type="button">
          <i class="fa-solid fa-minus"></i>
        </button>
        <span class="file-preview__zoom-level">{{ zoomLevel }}%</span>
        <button class="file-preview__zoom-btn" (click)="zoomIn()" 
                [disabled]="zoomLevel >= 200"
                title="Zoom in (Ctrl + +)" 
                aria-label="Zoom in" 
                type="button">
          <i class="fa-solid fa-plus"></i>
        </button>
        <button class="file-preview__zoom-btn" (click)="resetZoom()" 
                title="Reset zoom (Ctrl + 0)" 
                aria-label="Reset zoom" 
                type="button">
          <i class="fa-solid fa-arrows-to-dot"></i>
        </button>
      </div>

      <!-- Page Navigation (PDF only) -->
      <div class="file-preview__page-controls" *ngIf="previewData?.type === 'pdf' && pdfLoaded && totalPages > 1">
        <button class="file-preview__page-btn" (click)="previousPage()" 
                [disabled]="currentPage <= 1"
                title="Previous page (←)" 
                aria-label="Previous page" 
                type="button">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="file-preview__page-info">{{ currentPage }} / {{ totalPages }}</span>
        <button class="file-preview__page-btn" (click)="nextPage()" 
                [disabled]="currentPage >= totalPages"
                title="Next page (→)" 
                aria-label="Next page"
                type="button">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <span class="file-preview__page-indicator" *ngIf="(previewData?.type === 'pdf' || previewData?.type === 'docx') && totalPages > 0" 
        aria-label="Current page">
    Page {{ currentPage }} of {{ totalPages }}
  </span>
    </div>
  </section>

  <!-- ========================================
       CITATION INFO BAR (When in citation mode)
       ======================================== -->
  <section class="file-preview__citation-bar" *ngIf="isCitationMode && citationText && !isLoading && !errorMessage" 
           aria-label="Citation information">
    <div class="file-preview__citation-content">
      <div class="file-preview__citation-icon">
        <i class="fa-solid fa-quote-left"></i>
      </div>
      <div class="file-preview__citation-text">
        <div class="file-preview__citation-label">
          Highlighted Text<span *ngIf="previewData?.type !== 'txt' && citationPage"> (Page {{ citationPage }})</span>:
        </div>
        <div class="file-preview__citation-quote">"{{ citationText }}"</div>
      </div>
      <button class="file-preview__citation-copy" 
              (click)="copyCitation()" 
              title="Copy citation to clipboard"
              aria-label="Copy citation"
              type="button">
        <i class="fa-solid fa-copy"></i>
        Copy Citation
      </button>
    </div>
  </section>

  <!-- ========================================
       PANEL MAIN CONTENT
       ======================================== -->
  <main class="file-preview__content" role="main">
    <div class="file-preview__scroll" #scrollContainer>

      <!-- ========================================
           LOADING STATE
           ======================================== -->
      <ng-container *ngIf="isLoading">
        <div class="file-preview__loading" role="status" aria-live="polite" aria-label="Loading file">
          <div class="file-preview__loading-content">
            <div class="file-preview__spinner"></div>
            <p class="file-preview__loading-text">Loading file preview...</p>
          </div>
        </div>
      </ng-container>

      <!-- ========================================
           ERROR STATE
           ======================================== -->
      <ng-container *ngIf="errorMessage && !isLoading">
        <div class="file-preview__error" role="status" aria-live="polite">
          <div class="file-preview__error-content">
            <!-- Error Icon -->
            <div class="file-preview__error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" opacity="0.3" />
                <path d="M12 8V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <circle cx="12" cy="16" r="1" fill="currentColor" />
              </svg>
            </div>

            <!-- Error Message -->
            <div class="file-preview__error-text">
              <h3 class="file-preview__error-title">Failed to Load Preview</h3>
              <p class="file-preview__error-subtitle">{{ errorMessage }}</p>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- ========================================
           PDF PREVIEW
           ======================================== -->
      <ng-container *ngIf="!isLoading && !errorMessage && previewData?.type === 'pdf'">
        <div class="file-preview__pdf-container">
          <pdf-viewer 
            *ngIf="showPdfViewer && pdfSrc"
            [src]="pdfSrc"
            [render-text]="true"
            [original-size]="false"
            [zoom]="pdfZoom"
            [page]="pdfLoaded && firstPageRendered ? currentPage : 1"
            [show-all]="false"
            [stick-to-page]="true"
            [external-link-target]="'blank'"
            [autoresize]="true"
            [fit-to-page]="true"
            (after-load-complete)="onPdfLoadComplete($event)"
            (page-rendered)="onPageRendered($event)"
            (text-layer-rendered)="onTextLayerRendered($event)"
            (error)="onPdfError($event)"
            class="file-preview__pdf-viewer">
          </pdf-viewer>
        </div>
      </ng-container>

      <!-- ========================================
           DOCX PREVIEW (Using docx-preview library)
           ======================================== -->
      <ng-container *ngIf="!isLoading && !errorMessage && previewData?.type === 'docx'">
        <div class="file-preview__docx-container">
          <div #docxContainer 
               class="file-preview__docx-viewer"
               [style.transform]="'scale(' + (zoomLevel / 100) + ')'"
               [style.transform-origin]="'top left'"></div>
        </div>
      </ng-container>

      <!-- ========================================
           TXT PREVIEW
           ======================================== -->
      <ng-container *ngIf="!isLoading && !errorMessage && previewData?.type === 'txt'">
        <div class="file-preview__txt-container">
          <pre class="file-preview__txt-content" 
               [style.transform]="'scale(' + (zoomLevel / 100) + ')'"
               [style.transform-origin]="'top left'"
               [innerHTML]="(txtHtml || previewData?.content) | safeHtml"></pre>
        </div>
      </ng-container>

      <!-- ========================================
           UNSUPPORTED FILE TYPE
           ======================================== -->
      <ng-container *ngIf="!isLoading && !errorMessage && previewData?.type === 'unknown'">
        <div class="file-preview__unsupported" role="status">
          <div class="file-preview__unsupported-content">
            <div class="file-preview__unsupported-icon">
              <i class="fa-solid fa-file-circle-question"></i>
            </div>
            <div class="file-preview__unsupported-text">
              <h3 class="file-preview__unsupported-title">Preview Not Available</h3>
              <p class="file-preview__unsupported-subtitle">
                This file type is not supported for preview.
              </p>
            </div>
          </div>
        </div>
      </ng-container>

    </div>
  </main>
</div>

